'use client'

import { useCallback, useEffect, useState } from 'react'
import { ImageIcon, Loader2, Search } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
import type { SiteSearchResponse } from '@/lib/unified-site-search'
import { IconComponent } from '@/components/ui/icon'

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(t)
  }, [value, delay])

  return debounced
}

function SearchThumb({
  src,
  alt,
  className,
}: {
  src: string | null
  alt: string
  className?: string
}) {
  if (src) {
    return (
      <span
        className={cn(
          'relative size-10 shrink-0 overflow-hidden rounded border border-theme-surface-border bg-muted',
          className,
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt={alt} className="size-full object-cover" />
      </span>
    )
  }

  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded border border-theme-surface-border bg-muted text-muted-foreground',
        className,
      )}
      aria-hidden
    >
      <ImageIcon className="size-4 opacity-70" />
    </span>
  )
}

function StateSearchThumb({ iconSvg }: { iconSvg: string | null }) {
  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded border border-theme-surface-border bg-muted p-1.5 text-foreground',
      )}
      aria-hidden
    >
      {iconSvg ? (
        <IconComponent
          icon={iconSvg}
          containerClassName="flex size-full max-h-full max-w-full items-center justify-center [&_svg]:h-auto [&_svg]:w-full"
        />
      ) : (
        <ImageIcon className="size-4 text-muted-foreground opacity-70" />
      )}
    </span>
  )
}

const emptyResults: SiteSearchResponse = {
  talent: [],
  venues: [],
  events: [],
  blogs: [],
  states: [],
  pages: [],
}

type HeaderSiteSearchProps = {
  /** From Site global (Global Text); server-provided for static caching */
  searchEmptyPrompt: string
  searchInputPlaceholder: string
  searchLoadingMessage: string
}

export function HeaderSiteSearch({
  searchEmptyPrompt,
  searchInputPlaceholder,
  searchLoadingMessage,
}: HeaderSiteSearchProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query.trim(), 550)
  const [data, setData] = useState<SiteSearchResponse>(emptyResults)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (q: string) => {
    if (!q) {
      setData(emptyResults)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/site-search?${new URLSearchParams({ q })}`)
      const json = (await res.json()) as SiteSearchResponse & { error?: string }
      if (!res.ok) {
        setData(emptyResults)
        setError(json.error || 'Search failed.')
        return
      }
      setData({
        talent: json.talent ?? [],
        venues: json.venues ?? [],
        events: json.events ?? [],
        blogs: json.blogs ?? [],
        states: json.states ?? [],
        pages: json.pages ?? [],
      })
    } catch {
      setData(emptyResults)
      setError('Search failed.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) return
    void load(debounced)
  }, [open, debounced, load])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const close = () => {
    setOpen(false)
    setQuery('')
    setData(emptyResults)
    setError(null)
  }

  const hasResults =
    data.talent.length > 0
    || data.venues.length > 0
    || data.events.length > 0
    || data.blogs.length > 0
    || data.states.length > 0
    || data.pages.length > 0

  return (
    <>
      <button
        type="button"
        aria-label="Open search"
        onClick={() => setOpen(true)}
        className="group flex h-full w-13.75 shrink-0 cursor-pointer items-center justify-center border-l [border-left-width:var(--header-border-width)] border-theme-surface-border text-theme-button-secondary-foreground transition-colors hover:bg-theme-button-primary-background hover:text-theme-button-primary-foreground md:w-17.5"
      >
        <Search className="size-5" strokeWidth={2} />
      </button>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          if (next) setOpen(true)
          else close()
        }}
        title="Search"
        description="Search events, artists, venues, blog posts, states, and pages."
        className="sm:max-w-xl"
        showCloseButton
      >
        <CommandInput
          placeholder={searchInputPlaceholder}
          value={query}
          onValueChange={setQuery}
          className="border-0"
        />
        <CommandList className="max-h-[min(70vh,420px)]">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {searchLoadingMessage}
            </div>
          )}
          {error && !loading && <div className="px-3 py-4 text-sm text-red-600">{error}</div>}
          {!loading && !error && !debounced && (
            <div className="px-3 py-4 text-sm text-muted-foreground">{searchEmptyPrompt}</div>
          )}
          {!loading && !error && debounced && !hasResults && (
            <CommandEmpty>No results found.</CommandEmpty>
          )}

          {!loading && !error && debounced && hasResults && (
            <>
              {data.pages.length > 0 && (
                <CommandGroup heading="Pages">
                  {data.pages.map((item) => (
                    <CommandItem
                      key={item.id}
                      asChild
                      value={`page-${item.id}-${item.title}-${item.searchDescription ?? ''}`}
                    >
                      <a href={item.href} className="flex w-full items-center gap-3" onClick={close}>
                        {item.searchIconSvg ?
                          <StateSearchThumb iconSvg={item.searchIconSvg} />
                        : <SearchThumb src={item.imageUrl} alt={item.title} />}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{item.title}</span>
                          {item.searchDescription && (
                            <span className="text-muted-foreground block truncate text-xs">
                              {item.searchDescription}
                            </span>
                          )}
                        </span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.states.length > 0 && (
                <CommandGroup heading="States">
                  {data.states.map((item) => (
                    <CommandItem
                      key={item.id}
                      asChild
                      value={`state-${item.id}-${item.name}`}
                    >
                      <a href={item.href} className="flex w-full items-center gap-3" onClick={close}>
                        <StateSearchThumb iconSvg={item.iconSvg} />
                        <span className="min-w-0 flex-1 truncate">
                          {item.name}{' '}
                          <span className="text-muted-foreground">({item.abbreviation})</span>
                        </span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.talent.length > 0 && (
                <CommandGroup heading="Talent">
                  {data.talent.map((item) => (
                    <CommandItem key={item.id} asChild value={`talent-${item.id}-${item.name}`}>
                      <a
                        href={item.href}
                        className="flex w-full items-center gap-3"
                        onClick={close}
                      >
                        <SearchThumb src={item.imageUrl} alt={item.name} />
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.venues.length > 0 && (
                <CommandGroup heading="Venues">
                  {data.venues.map((item) => (
                    <CommandItem key={item.id} asChild value={`venue-${item.id}-${item.name}`}>
                      <a
                        href={`/venues/${item.slug}`}
                        className="flex w-full items-center gap-3"
                        onClick={close}
                      >
                        <SearchThumb src={item.imageUrl} alt={item.name} />
                        <span className="min-w-0 flex-1 truncate">{item.name}</span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.events.length > 0 && (
                <CommandGroup heading="Events">
                  {data.events.map((item) => (
                    <CommandItem
                      key={item.id}
                      asChild
                      value={`event-${item.id}-${item.name}-${item.dateLine ?? ''}-${item.subtitle ?? ''}`}
                    >
                      <a
                        href={`/events/${item.id}`}
                        className="flex w-full items-center gap-3"
                        onClick={close}
                      >
                        <SearchThumb src={item.imageUrl} alt={item.name} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{item.name}</span>
                          {item.dateLine && (
                            <span className="text-muted-foreground block truncate text-xs">
                              {item.dateLine}
                            </span>
                          )}
                          {item.subtitle && (
                            <span className="text-muted-foreground block truncate text-xs">
                              {item.subtitle}
                            </span>
                          )}
                        </span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {data.blogs.length > 0 && (
                <CommandGroup heading="Blog">
                  {data.blogs.map((item) => (
                    <CommandItem key={item.id} asChild value={`blog-${item.id}-${item.title}`}>
                      <a href={item.href} className="flex w-full items-center gap-3" onClick={close}>
                        <SearchThumb src={item.imageUrl} alt={item.title} />
                        <span className="min-w-0 flex-1 truncate">{item.title}</span>
                      </a>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  )
}
